import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Star, CheckCircle2, Loader2, Camera, ThumbsUp, ThumbsDown, ImageOff } from "lucide-react";
import { formatApiErrorDetail } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

export default function PublicReview() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState("");
  const [wouldRecommend, setWouldRecommend] = useState(null);
  const [displayMode, setDisplayMode] = useState("full");
  const [allowPublic, setAllowPublic] = useState(true);
  const [photoFileId, setPhotoFileId] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/reviews/${token}`);
        setData(data);
        if (data.already_submitted) setSubmitted(true);
      } catch (err) {
        setError(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't load this review link.");
      } finally { setLoading(false); }
    })();
  }, [token]);

  async function handlePhoto(file) {
    if (!file) return;
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/reviews/${token}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotoFileId(data.file_id);
      toast.success("Photo added");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally { setPhotoUploading(false); }
  }

  async function submit() {
    if (rating < 1) return toast.error("Please pick a star rating");
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews/${token}`, {
        rating, comment: comment.trim(),
        would_recommend: wouldRecommend,
        display_name_mode: displayMode,
        allow_public: allowPublic,
        photo_file_id: photoFileId || undefined,
      });
      setSubmitted(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed");
    } finally { setSubmitting(false); }
  }

  if (loading) return <Shell><Centered><Loader2 className="w-5 h-5 animate-spin text-stone-400" /></Centered></Shell>;
  if (error) return <Shell><Centered><ImageOff className="w-10 h-10 text-stone-400 mb-3" /><div className="font-serif text-2xl mb-1">{error}</div></Centered></Shell>;

  if (submitted) {
    return (
      <Shell>
        <div className="max-w-xl mx-auto text-center pt-12" data-testid="review-submitted">
          <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-600 mb-4" />
          <h1 className="font-serif text-4xl font-light mb-3">Thank you{data?.customer_name ? `, ${data.customer_name.split(" ")[0]}` : ""}!</h1>
          <p className="text-stone-600 max-w-md mx-auto">Your feedback means the world to us. With your permission, we may share a snippet on our website to help others find {data?.studio_name || "us"}.</p>
        </div>
      </Shell>
    );
  }

  const stars = [1, 2, 3, 4, 5];
  return (
    <Shell>
      <div className="max-w-xl mx-auto" data-testid="review-form">
        <div className="text-center mb-8">
          <div className="eyebrow mb-3 text-stone-500">{data.studio_name}</div>
          <h1 className="font-serif text-4xl sm:text-5xl font-light">How was your session?</h1>
          <p className="text-stone-600 mt-3">Hi {data.customer_name?.split(" ")[0]}, your honest feedback helps us grow.</p>
          {data.service_name && <p className="text-stone-400 text-sm mt-2">{data.service_name}</p>}
        </div>

        <div className="bg-white border border-stone-200 p-6 sm:p-8 space-y-7 shadow-sm">
          {/* Stars */}
          <div className="text-center">
            <label className="block text-xs uppercase tracking-[0.18em] text-stone-500 mb-3">Your rating *</label>
            <div className="inline-flex gap-2" data-testid="rating-stars">
              {stars.map((n) => {
                const active = (hoverRating || rating) >= n;
                return (
                  <button
                    key={n}
                    onMouseEnter={() => setHoverRating(n)}
                    onMouseLeave={() => setHoverRating(0)}
                    onClick={() => setRating(n)}
                    aria-label={`${n} star${n > 1 ? "s" : ""}`}
                    className="transition-transform hover:scale-110"
                    data-testid={`star-${n}`}
                  >
                    <Star className={`w-9 h-9 sm:w-11 sm:h-11 ${active ? "fill-amber-400 text-amber-400" : "text-stone-300"}`} />
                  </button>
                );
              })}
            </div>
            {rating > 0 && <div className="text-xs text-stone-500 mt-2">You picked {rating} of 5</div>}
          </div>

          {/* Comment */}
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-stone-500">Your experience</span>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={5}
              placeholder="What did you love? Anything we could improve?"
              className="mt-2 w-full bg-transparent border border-stone-300 p-3 outline-none focus:border-stone-900 text-sm leading-relaxed"
              data-testid="review-comment"
            />
          </label>

          {/* Recommend */}
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-stone-500 mb-3">Would you recommend us?</div>
            <div className="flex gap-2">
              <button
                onClick={() => setWouldRecommend(true)}
                className={`flex-1 inline-flex items-center justify-center gap-2 px-4 h-11 border ${
                  wouldRecommend === true ? "bg-emerald-600 text-white border-emerald-600" : "border-stone-300 hover:border-stone-900"
                }`}
                data-testid="recommend-yes"
              ><ThumbsUp className="w-4 h-4" /> Yes, definitely</button>
              <button
                onClick={() => setWouldRecommend(false)}
                className={`flex-1 inline-flex items-center justify-center gap-2 px-4 h-11 border ${
                  wouldRecommend === false ? "bg-stone-900 text-stone-50 border-stone-900" : "border-stone-300 hover:border-stone-900"
                }`}
                data-testid="recommend-no"
              ><ThumbsDown className="w-4 h-4" /> Not really</button>
            </div>
          </div>

          {/* Optional photo */}
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-stone-500 mb-3">Add a photo (optional)</div>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => handlePhoto(e.target.files?.[0])} data-testid="review-photo-input" />
            {photoFileId ? (
              <div className="flex items-center gap-3">
                <img src={`${BACKEND}/api/files/${photoFileId}?w=200`} alt="" className="w-20 h-20 object-cover border border-stone-300" />
                <button onClick={() => { setPhotoFileId(null); }} className="text-xs text-stone-500 hover:text-stone-900 underline">Remove</button>
              </div>
            ) : (
              <button
                onClick={() => fileRef.current?.click()}
                disabled={photoUploading}
                className="inline-flex items-center gap-2 px-4 h-10 border border-stone-300 hover:border-stone-900 text-xs uppercase tracking-[0.18em] disabled:opacity-60"
                data-testid="review-photo-btn"
              >
                {photoUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                {photoUploading ? "Uploading…" : "Upload a photo"}
              </button>
            )}
          </div>

          {/* Display name + public */}
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-stone-500 mb-3">How should we show your name?</div>
            <div className="grid grid-cols-3 gap-2">
              {[
                ["full", `Full · "${data.customer_name}"`],
                ["first", `First only · "${data.customer_name?.split(" ")[0]}"`],
                ["anon", "Anonymous"],
              ].map(([v, label]) => (
                <button
                  key={v}
                  onClick={() => setDisplayMode(v)}
                  className={`px-3 h-11 text-xs border text-left leading-tight ${displayMode === v ? "bg-stone-900 text-stone-50 border-stone-900" : "border-stone-300 hover:border-stone-900"}`}
                  data-testid={`display-mode-${v}`}
                >{label}</button>
              ))}
            </div>
          </div>

          <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="allow-public-label">
            <input
              type="checkbox"
              checked={allowPublic}
              onChange={(e) => setAllowPublic(e.target.checked)}
              className="mt-1"
              data-testid="allow-public-checkbox"
            />
            <span className="text-stone-700">Share my review publicly on the {data.studio_name} website to help other clients. <span className="text-stone-500">(Uncheck if you'd prefer to keep this private — we still get to read it.)</span></span>
          </label>

          <button
            onClick={submit}
            disabled={submitting || rating < 1}
            className="w-full h-12 bg-stone-900 hover:bg-stone-800 text-stone-50 text-xs uppercase tracking-[0.22em] disabled:opacity-50"
            data-testid="submit-review-btn"
          >
            {submitting ? "Sending…" : "Submit feedback"}
          </button>
        </div>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-stone-50 text-stone-900 py-12 px-4">
      <Toaster richColors position="top-center" />
      {children}
    </div>
  );
}

function Centered({ children }) {
  return <div className="max-w-md mx-auto text-center pt-16 flex flex-col items-center">{children}</div>;
}
